import type { Good } from "@/entities/good";
import type { GoodVersion } from "@/entities/goodVersion";
import { normalizeStoredImageRef } from "@/utils/imageRef";

function versionCreatedAtMs(version: GoodVersion): number {
  if (!version.createdAt) return 0;
  const t = new Date(version.createdAt).getTime();
  return Number.isFinite(t) ? t : 0;
}

/** Версия для отображения в списке/карточке: явный id → активная → самая новая по дате. */
export function resolveDisplayGoodVersion(good: Good | null | undefined, preferredVersionId?: string | null): GoodVersion | null {
  const versions = good?.versions ?? [];
  if (!versions.length) return null;

  if (preferredVersionId) {
    const preferred = versions.find((v) => v.id === preferredVersionId);
    if (preferred) return preferred;
  }

  const active = versions.find((v) => v.isActive);
  if (active) return active;

  return [...versions].sort((a, b) => versionCreatedAtMs(b) - versionCreatedAtMs(a))[0] ?? null;
}

export function normalizeGoodImageList(images: unknown): string[] {
  if (!Array.isArray(images)) return [];
  return images
    .filter((image): image is string => typeof image === "string" && Boolean(image.trim()))
    .map((image) => normalizeStoredImageRef(image));
}

/** Берём более полный список URL: из версии или с уровня товара (после черновика на шаге 2). */
export function resolveVersionImageUrls(version: GoodVersion | null | undefined, good?: Good | null): string[] {
  const fromVersion = normalizeGoodImageList(version?.media?.images);
  const fromGood = normalizeGoodImageList(good?.images);
  return fromVersion.length >= fromGood.length ? fromVersion : fromGood;
}

/** Если в payload версии меньше картинок, чем на товаре — дополняем из good.images. */
export function enrichGoodVersionImages(version: GoodVersion, goodImages: unknown): GoodVersion {
  const versionImages = normalizeGoodImageList(version.media?.images);
  const goodLevelImages = normalizeGoodImageList(goodImages);
  if (goodLevelImages.length <= versionImages.length) return version;
  return {
    ...version,
    media: {
      ...version.media,
      images: goodLevelImages,
    },
  };
}
